import { useState, useEffect } from "react";
import VpsUploadField from "@/components/base/VpsUploadField";

export type InvMusic = {
  id: string;
  tenant_id: string;
  name: string;
  artist: string | null;
  music_url: string;
  duration: string | null;
  genre: string | null;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
};

const GENRES = ["Clásica", "Romántica", "Pop", "Jazz", "Electrónica", "Instrumental", "Religiosa", "Tropical", "Otro"];

interface Props {
  music: InvMusic | null;
  isNew: boolean;
  tenantId: string;
  onClose: () => void;
  onSave: (data: Partial<InvMusic>) => void;
}

const BLANK: Partial<InvMusic> = {
  name: "",
  artist: "",
  music_url: "",
  duration: "",
  genre: "Romántica",
  is_active: true,
  sort_order: 0,
};

export default function MusicModal({ music, isNew, tenantId, onClose, onSave }: Props) {
  const [form, setForm] = useState<Partial<InvMusic>>(BLANK);
  const [playing, setPlaying]   = useState(false);
  const [audio, setAudio]       = useState<HTMLAudioElement | null>(null);

  useEffect(() => {
    setForm(isNew ? BLANK : { ...music });
    setPlaying(false);
    if (audio) { audio.pause(); setAudio(null); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [music, isNew]);

  const set = <K extends keyof InvMusic>(key: K, val: InvMusic[K]) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const handlePreview = () => {
    if (!form.music_url) return;
    if (playing && audio) {
      audio.pause();
      setPlaying(false);
      return;
    }
    const a = new Audio(form.music_url);
    a.play().catch(() => {});
    a.onended = () => setPlaying(false);
    setAudio(a);
    setPlaying(true);
  };

  const isValid = (form.name ?? "").trim().length > 0 && (form.music_url ?? "").trim().length > 0;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "Agregar Música" : "Editar Música"}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">Canción para invitaciones</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-700 rounded-lg transition-colors cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Nombre de la Canción <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={form.name ?? ""}
              onChange={(e) => set("name", e.target.value)}
              placeholder="A Thousand Years"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Artist */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Artista</label>
            <input
              type="text"
              value={form.artist ?? ""}
              onChange={(e) => set("artist", e.target.value)}
              placeholder="Christina Perri"
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
            />
          </div>

          {/* Music Upload */}
          <div>
            <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">
              Archivo de Audio <span className="text-red-400">*</span>
            </label>
            <VpsUploadField
              value={form.music_url ?? null}
              onChange={(url) => set("music_url", url ?? "")}
              tenantId={tenantId}
              entityType="invitation_music"
              entityId={music?.id}
              accept="audio/*"
              showPreview
              previewType="audio"
              helperText="Sube un archivo MP3 o similar. Máx. recomendado 5MB."
              folder="invitations"
              maxSizeMB={10}
            />
            {form.music_url && (
              <div className="flex items-center gap-2 mt-2">
                <button
                  onClick={handlePreview}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all cursor-pointer ${
                    playing ? "bg-amber-50 border-amber-300 text-amber-600" : "border-slate-200 text-slate-400 hover:text-[#C8A96E] hover:border-[#C8A96E]/30"
                  }`}
                  title={playing ? "Detener" : "Previsualizar"}
                >
                  <i className={playing ? "ri-stop-line" : "ri-play-line"}></i>
                  {playing ? "Detener" : "Previsualizar"}
                </button>
              </div>
            )}
            {playing && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                <div className="flex gap-0.5">
                  {[1,2,3].map((i) => (
                    <div key={i} className="w-0.5 bg-amber-500 rounded-full animate-pulse" style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 0.15}s` }} />
                  ))}
                </div>
                <span>Reproduciendo…</span>
              </div>
            )}
          </div>

          {/* Duration + Genre */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Duración</label>
              <input
                type="text"
                value={form.duration ?? ""}
                onChange={(e) => set("duration", e.target.value)}
                placeholder="4:35"
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#C8A96E]/50 transition-colors"
              />
            </div>
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Género</label>
              <select
                value={form.genre ?? "Romántica"}
                onChange={(e) => set("genre", e.target.value)}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50 appearance-none cursor-pointer"
              >
                {GENRES.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Sort + Active */}
          <div className="flex items-center gap-6">
            <div>
              <label className="block text-slate-500 text-xs font-medium uppercase tracking-wider mb-1.5">Orden</label>
              <input
                type="number"
                value={form.sort_order ?? 0}
                onChange={(e) => set("sort_order", Number(e.target.value))}
                min={0}
                className="w-20 border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#C8A96E]/50"
              />
            </div>
            <label className="flex items-center gap-2 cursor-pointer mt-5">
              <div
                onClick={() => set("is_active", !form.is_active)}
                className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${form.is_active ? "bg-emerald-500" : "bg-slate-200"}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.is_active ? "translate-x-4" : "translate-x-0.5"}`} />
              </div>
              <span className="text-sm text-slate-600">Activa en el catálogo</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-500 hover:text-slate-700 border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={() => isValid && onSave(form)}
            disabled={!isValid}
            className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8965a] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-save-line"></i></span>
            {isNew ? "Agregar Canción" : "Guardar Cambios"}
          </button>
        </div>
      </div>
    </div>
  );
}